/**
 * @file TrendChart.tsx
 * @brief Theme-aware Recharts wrapper used by Analytics / Dashboard.
 *
 * @details
 * - Multi-series area chart using HSL CSS variables (light/dark).
 * - Per-series formatting via `series[].format`.
 * - `variant="card"` (default) — soft Card surface for reports etc.
 * - `variant="cell"` — flat header + chart for `GridBlock` cells.
 * - Each chart shows the range total and how the later half compares with
 *   the earlier half. The plot is a smooth area with a solid hairline grid,
 *   a stroke that strengthens toward the latest point, and an end dot.
 * - Gradient ids are unique per mount so two charts that share a data key
 *   (user signups and company signups both use `count`) cannot paint each
 *   other.
 *
 * @dependencies
 * - recharts
 * - components/ui/Card
 * - shared/utils/formatters
 * - ./trendChartMath
 *
 * @author DES
 * @date 26-05-2026
 * @updated 28-09-2026 — dashboard chart look: totals, hairline plot, unique fills
 * @updated 03-10-2026: loading skeleton drawn in the chart's own shape
 */

import { useId, useMemo, type ReactNode } from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { TrendingDown, TrendingUp } from "lucide-react";
import { Skeleton } from "@/components/ui/Skeleton";
import { Card, CardContent, CardHeader } from "@/components/ui/Card";
import { cn } from "@/shared/theme/cn";
import { formatDate, toDate } from "@/shared/utils/formatters";
import {
  finiteNumber,
  formatTrendDelta,
  summarizeTrend,
  trendYDomain,
  type TrendSummary,
} from "./trendChartMath";

/**
 * Loading skeleton = the chart's own shape (03-10-2026, DES: the old bar
 * skeleton did not match the area chart). Same plot margins as the
 * AreaChart, the same hairline grid, a smooth grey area with an end dot, and
 * bones where the axis labels and the Total sit, so nothing moves when the
 * data lands. Points are y in % of the plot (0 = top): a calm rise.
 */
const SKELETON_POINTS = [64, 56, 60, 49, 53, 42, 46, 36, 40, 29, 32];
/** Recharts' plot inset for this chart: margin top/right + default X axis height. */
const PLOT_TOP = 16;
const PLOT_RIGHT = 16;
const PLOT_BOTTOM = 30;
/** Five grid lines, as the Y axis' five default ticks draw. */
const GRID_STOPS = [0, 25, 50, 75, 100];
const SKELETON_X_TICKS = 5;

/** Smooth (Catmull-Rom → Bézier) path through the points, in a 100×100 box. */
function skeletonPaths(points: number[]): { line: string; area: string } {
  const step = 100 / (points.length - 1);
  const pts = points.map((y, i) => [i * step, y] as const);
  let line = `M${pts[0][0]},${pts[0][1]}`;
  for (let i = 0; i < pts.length - 1; i += 1) {
    const p0 = pts[Math.max(0, i - 1)];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[Math.min(pts.length - 1, i + 2)];
    const c1 = [p1[0] + (p2[0] - p0[0]) / 6, p1[1] + (p2[1] - p0[1]) / 6];
    const c2 = [p2[0] - (p3[0] - p1[0]) / 6, p2[1] - (p3[1] - p1[1]) / 6];
    line += ` C${c1[0]},${c1[1]} ${c2[0]},${c2[1]} ${p2[0]},${p2[1]}`;
  }
  return { line, area: `${line} L100,100 L0,100 Z` };
}

const SKELETON_PATHS = skeletonPaths(SKELETON_POINTS);
const SKELETON_TONE = "hsl(var(--app-quaternary-dark))";

export interface TrendSeries {
  dataKey: string;
  label: string;
  color: string;
  format?: (value: number) => string;
}

interface TrendChartProps<T extends Record<string, unknown>> {
  title: string;
  subtitle?: ReactNode;
  data: T[];
  xKey?: string;
  series: TrendSeries[];
  isLoading?: boolean;
  height?: number;
  emptyText?: string;
  className?: string;
  /** Flat grid-cell surface when nested in `GridCell`. Default `card`. */
  variant?: "card" | "cell";
}

interface TrendTooltipRow {
  dataKey?: string | number;
  name?: string | number;
  value?: unknown;
  color?: string;
}

const AXIS_TICK = {
  fill: "hsl(var(--muted-foreground))",
  fontSize: 11,
} as const;

const DELTA_HINT = "Later half of this range compared with the earlier half";

function formatXTick(raw: unknown): string {
  if (typeof raw !== "string") return String(raw ?? "");
  const d = toDate(raw);
  if (d) return formatDate(d, "d MMM");
  return raw;
}

function compactAxisNumber(value: number): string {
  return Intl.NumberFormat("en-GB", {
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(value);
}

function axisTickLabel(value: number, format?: (n: number) => string): string {
  if (!Number.isFinite(value)) return "";
  if (!format) return compactAxisNumber(value);
  return format(value).replace(/\.00$/, "");
}

function TrendDelta({ deltaPct }: { deltaPct: number | null }) {
  if (deltaPct == null) return null;
  const Icon = deltaPct > 0 ? TrendingUp : deltaPct < 0 ? TrendingDown : null;
  return (
    <p
      className="mt-1.5 text-[11px] text-muted-foreground"
      title={DELTA_HINT}
    >
      <span className="inline-flex items-center gap-0.5 font-medium tabular-nums">
        {Icon ? <Icon className="h-3 w-3" aria-hidden /> : null}
        {formatTrendDelta(deltaPct)}
      </span>
      {" vs first half"}
    </p>
  );
}

function SeriesTotal({
  summary,
  prominent,
}: {
  summary: TrendSummary;
  prominent: boolean;
}) {
  return (
    <div className={cn("min-w-0", prominent && "text-right")}>
      {prominent ? (
        <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
          Total
        </p>
      ) : null}
      <p
        className={cn(
          "font-semibold leading-none tracking-tight tabular-nums",
          prominent ? "mt-1 text-2xl" : "mt-0.5 text-sm",
        )}
      >
        {summary.formatted}
      </p>
      <TrendDelta deltaPct={summary.deltaPct} />
    </div>
  );
}

function ChartHeading({
  title,
  subtitle,
  series,
  summaries,
  titleClassName,
}: {
  title: string;
  subtitle?: ReactNode;
  series: TrendSeries[];
  summaries: TrendSummary[];
  titleClassName: string;
}) {
  const single = series.length === 1;
  const lead = series[0];
  const leadSummary = summaries[0];

  return (
    <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
      <div className="min-w-0 space-y-1">
        <h3 className={cn("flex items-center gap-2", titleClassName)}>
          {single && lead ? (
            <span
              className="h-2 w-2 shrink-0 rounded-full"
              style={{ background: lead.color }}
              aria-hidden
            />
          ) : null}
          {title}
        </h3>
        {subtitle ? (
          <p className="text-sm text-muted-foreground">{subtitle}</p>
        ) : null}
      </div>
      {single && leadSummary ? (
        <SeriesTotal summary={leadSummary} prominent />
      ) : (
        <div className="flex flex-wrap gap-x-5 gap-y-3">
          {series.map((s, i) => {
            const summary = summaries[i];
            if (!summary) return null;
            return (
              <div key={s.dataKey} className="min-w-0">
                <p className="flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground">
                  <span
                    className="h-1.5 w-1.5 shrink-0 rounded-full"
                    style={{ background: s.color }}
                    aria-hidden
                  />
                  {s.label}
                </p>
                <SeriesTotal summary={summary} prominent={false} />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function tooltipRowsFrom(payload: unknown): TrendTooltipRow[] {
  if (!Array.isArray(payload)) return [];
  return payload.filter(
    (item): item is TrendTooltipRow => item != null && typeof item === "object",
  );
}

function TrendTooltip({
  active,
  payload,
  label,
  series,
}: {
  active?: boolean;
  payload?: unknown;
  label?: unknown;
  series: TrendSeries[];
}) {
  const rows = tooltipRowsFrom(payload);
  if (!active || rows.length === 0) return null;
  return (
    <div className="min-w-[10.5rem] rounded-2xl border border-border/80 bg-popover/95 px-3 py-2.5 shadow-lg backdrop-blur-sm">
      <p className="text-[11px] font-medium text-muted-foreground">
        {formatXTick(label)}
      </p>
      <ul className="mt-1.5 space-y-1">
        {rows.map((row, index) => {
          const dataKey = String(row.dataKey ?? "series");
          const key = `${dataKey}-${index}`;
          // Match on the series' own key (was the row key, which never matched,
          // so money showed as raw pence and every row as grey).
          const match = series.find((s) => s.dataKey === dataKey);
          const value = finiteNumber(row.value);
          const shown =
            value == null
              ? "—"
              : match?.format
                ? match.format(value)
                : value.toLocaleString("en-GB");
          return (
            <li key={key} className="flex items-center gap-2 text-xs">
              <span
                className="h-3 w-0.5 shrink-0 rounded-full"
                style={{ background: match?.color ?? "hsl(var(--foreground))" }}
                aria-hidden
              />
              <span className="text-muted-foreground">
                {match?.label ?? String(row.name ?? "")}
              </span>
              <span className="ml-auto pl-3 font-medium tabular-nums">{shown}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function EndDot(props: {
  cx?: number;
  cy?: number;
  index?: number;
  color: string;
  lastIndex: number;
}) {
  const { cx, cy, index, color, lastIndex } = props;
  if (cx == null || cy == null || index !== lastIndex) return <g />;
  return (
    <g>
      <circle cx={cx} cy={cy} r={7} fill={color} fillOpacity={0.18} />
      <circle
        cx={cx}
        cy={cy}
        r={3.25}
        fill="hsl(var(--background))"
        stroke={color}
        strokeWidth={2}
      />
    </g>
  );
}

/** A bone the size of its text: same font and line box as the real label. */
function TextBone({ children }: { children: ReactNode }) {
  return (
    <span className="select-none rounded bg-app-quaternary-dark text-transparent">
      {children}
    </span>
  );
}

/**
 * Bones where ChartHeading's totals sit, built from SeriesTotal's own text
 * classes so the heading is exactly as tall loading as loaded.
 */
function TotalsSkeleton({ count }: { count: number }) {
  const total = (prominent: boolean) => (
    <div className={cn("min-w-0", prominent && "text-right")}>
      {prominent ? (
        <p className="text-[10px] font-semibold uppercase tracking-[0.14em]">
          <TextBone>Total</TextBone>
        </p>
      ) : null}
      <p
        className={cn(
          "font-semibold leading-none tracking-tight tabular-nums",
          prominent ? "mt-1 text-2xl" : "mt-0.5 text-sm",
        )}
      >
        <TextBone>{prominent ? "£000.00" : "£00.00"}</TextBone>
      </p>
      <p className="mt-1.5 text-[11px]">
        <span className="inline-flex items-center gap-0.5 font-medium">
          <span className="h-3 w-0" />
          <TextBone>+00% vs first half</TextBone>
        </span>
      </p>
    </div>
  );
  return (
    <div className="animate-pulse" aria-hidden>
      {count <= 1 ? (
        total(true)
      ) : (
        <div className="flex flex-wrap gap-x-5 gap-y-3">
          {Array.from({ length: count }, (_, i) => (
            <div key={i} className="min-w-0">
              <p className="text-[11px] font-medium">
                <TextBone>Series</TextBone>
              </p>
              {total(false)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** The plot while loading: real grid lines, a grey area, axis-label bones. */
function TrendChartSkeleton({ height, yWidth }: { height: number; yWidth: number }) {
  const last = SKELETON_POINTS[SKELETON_POINTS.length - 1];
  return (
    <div role="status" aria-label="Loading chart" className="relative" style={{ height }}>
      <div
        className="absolute"
        style={{ top: PLOT_TOP, right: PLOT_RIGHT, bottom: PLOT_BOTTOM, left: yWidth }}
      >
        {GRID_STOPS.map((stop) => (
          <div
            key={stop}
            className="absolute inset-x-0 h-px bg-border/80"
            style={{ top: `${stop}%` }}
          />
        ))}
        <div className="absolute inset-0 animate-pulse">
          <svg
            className="absolute inset-0 h-full w-full overflow-visible"
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
            aria-hidden
          >
            <path d={SKELETON_PATHS.area} fill={SKELETON_TONE} fillOpacity={0.35} />
            <path
              d={SKELETON_PATHS.line}
              fill="none"
              stroke={SKELETON_TONE}
              strokeWidth={2.5}
              strokeLinecap="round"
              vectorEffect="non-scaling-stroke"
            />
          </svg>
          <span
            className="absolute h-[7px] w-[7px] -translate-x-1/2 -translate-y-1/2 rounded-full"
            style={{ left: "100%", top: `${last}%`, background: SKELETON_TONE }}
          />
        </div>
      </div>
      <div className="animate-pulse" aria-hidden>
        {GRID_STOPS.map((stop) => (
          <Skeleton
            key={stop}
            still
            className="absolute h-2 -translate-y-1/2"
            style={{
              top: `calc(${PLOT_TOP}px + (100% - ${PLOT_TOP + PLOT_BOTTOM}px) * ${stop / 100})`,
              right: `calc(100% - ${yWidth - 8}px)`,
              width: Math.min(28, yWidth - 14),
            }}
          />
        ))}
        <div
          className="absolute flex justify-between"
          style={{ left: yWidth, right: PLOT_RIGHT, bottom: 8 }}
        >
          {Array.from({ length: SKELETON_X_TICKS }, (_, i) => (
            <Skeleton key={i} still className="h-2 w-9" />
          ))}
        </div>
      </div>
    </div>
  );
}

export function TrendChart<T extends Record<string, unknown>>({
  title,
  subtitle,
  data,
  xKey = "period",
  series,
  isLoading,
  height = 280,
  emptyText = "No data for this range yet.",
  className,
  variant = "card",
}: TrendChartProps<T>) {
  const chartId = useId().replace(/:/g, "");
  const memoSeries = useMemo(() => series, [series]);
  const summaries = useMemo(
    () =>
      memoSeries.map((s) =>
        summarizeTrend(data as ReadonlyArray<Record<string, unknown>>, s.dataKey, s.format),
      ),
    [data, memoSeries],
  );
  const axisFormat = memoSeries[0]?.format;
  const yWidth = axisFormat ? 88 : 40;

  const chartBody = isLoading ? (
    <TrendChartSkeleton height={height} yWidth={yWidth} />
  ) : data.length === 0 ? (
    <div
      style={{ height }}
      className="flex items-center justify-center text-sm text-muted-foreground"
    >
      {emptyText}
    </div>
  ) : (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart
        data={data}
        margin={{ top: 16, right: 16, left: 0, bottom: 0 }}
      >
        <defs>
          {memoSeries.flatMap((s) => {
            const id = `${chartId}-${s.dataKey}`;
            return [
              <linearGradient
                key={`${id}-fill`}
                id={`${id}-fill`}
                x1="0"
                y1="0"
                x2="0"
                y2="1"
              >
                <stop offset="0%" stopColor={s.color} stopOpacity={0.28} />
                <stop offset="55%" stopColor={s.color} stopOpacity={0.08} />
                <stop offset="100%" stopColor={s.color} stopOpacity={0} />
              </linearGradient>,
              <linearGradient
                key={`${id}-stroke`}
                id={`${id}-stroke`}
                x1="0"
                y1="0"
                x2="1"
                y2="0"
              >
                <stop offset="0%" stopColor={s.color} stopOpacity={0.55} />
                <stop offset="100%" stopColor={s.color} stopOpacity={1} />
              </linearGradient>,
            ];
          })}
        </defs>
        <CartesianGrid
          vertical={false}
          stroke="hsl(var(--border))"
          strokeOpacity={0.85}
        />
        <XAxis
          dataKey={xKey}
          tickFormatter={formatXTick}
          tickLine={false}
          axisLine={false}
          tick={AXIS_TICK}
          minTickGap={28}
          interval="preserveStartEnd"
          dy={6}
        />
        <YAxis
          tickLine={false}
          axisLine={false}
          tick={{ ...AXIS_TICK, fontSize: 10 }}
          width={yWidth}
          domain={([dataMin, dataMax]: [number, number]) =>
            trendYDomain(dataMin, dataMax)
          }
          tickFormatter={(v: number) => axisTickLabel(v, axisFormat)}
        />
        <Tooltip
          cursor={{
            stroke: "hsl(var(--foreground))",
            strokeOpacity: 0.14,
            strokeWidth: 1,
          }}
          content={(props) => (
            <TrendTooltip
              active={props.active}
              payload={props.payload}
              label={props.label}
              series={memoSeries}
            />
          )}
          wrapperStyle={{ outline: "none", zIndex: 20 }}
        />
        {memoSeries.map((s) => {
          const id = `${chartId}-${s.dataKey}`;
          return (
            <Area
              key={s.dataKey}
              type="monotone"
              dataKey={s.dataKey}
              name={s.label}
              stroke={`url(#${id}-stroke)`}
              strokeWidth={2.5}
              strokeLinejoin="round"
              strokeLinecap="round"
              fill={`url(#${id}-fill)`}
              fillOpacity={1}
              baseValue={0}
              dot={
                <EndDot
                  color={s.color}
                  lastIndex={Math.max(0, data.length - 1)}
                />
              }
              activeDot={{
                r: 4,
                strokeWidth: 2,
                stroke: s.color,
                fill: "hsl(var(--background))",
              }}
              isAnimationActive
              animationDuration={700}
              animationEasing="ease-out"
            />
          );
        })}
      </AreaChart>
    </ResponsiveContainer>
  );

  const heading = isLoading ? (
    <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
      <div className="space-y-1">
        <h3 className="text-base font-semibold leading-none tracking-tight">
          {title}
        </h3>
        {subtitle ? (
          <p className="text-sm text-muted-foreground">{subtitle}</p>
        ) : null}
      </div>
      <TotalsSkeleton count={memoSeries.length} />
    </div>
  ) : data.length === 0 ? (
      <div className="space-y-1">
        <h3 className="text-base font-semibold leading-none tracking-tight">
          {title}
        </h3>
        {subtitle ? (
          <p className="text-sm text-muted-foreground">{subtitle}</p>
        ) : null}
      </div>
    ) : (
      <ChartHeading
        title={title}
        subtitle={subtitle}
        series={memoSeries}
        summaries={summaries}
        titleClassName="text-base font-semibold leading-none tracking-tight"
      />
    );

  if (variant === "cell") {
    return (
      <div className={cn("flex h-full flex-col gap-3", className)}>
        {heading}
        {chartBody}
      </div>
    );
  }

  return (
    <Card className={cn("border-0 bg-secondary/40 shadow-none", className)}>
      <CardHeader className="space-y-0">{heading}</CardHeader>
      <CardContent>{chartBody}</CardContent>
    </Card>
  );
}
